/**
 * One task (#293 AC07, AC10, AC12 to AC14, AC19): its facts, its proof and its whole history, completion
 * with the required proof or a manager's exception, reopen, notes and follow-up (never sent), delete with
 * Angre. A note typed but not added is never lost silently: completing or closing asks first.
 */
import { ApiError } from '@/api/client';
import { type ApiTask, type TaskEvent, useTask, useTaskWrites } from '@/api/tasks';
import { T } from '@/i18n';
import { oslo } from '@/rules';
import { Button, Drawer, Skeleton, toast } from '@/ui';
import { dayMonth } from '@/ui/format';
import { useRef, useState } from 'react';
import { SHOWN_LABEL, estimateOf, shownOf } from './model';

const KIND: Record<TaskEvent['kind'], string> = {
  created: 'Opprettet',
  edited: 'Endret',
  comment: 'Notat',
  followup: 'Oppfølging, ikke sendt',
  complete: 'Utført',
  exception: 'Lederunntak',
  reopen: 'Gjenåpnet',
  deleted: 'Slettet',
  restored: 'Gjenopprettet',
};

const when = (iso: string) => {
  const o = oslo(iso);
  return o ? `${dayMonth(o.date)} ${o.time}` : '';
};

export function TaskDetail({
  id,
  mayEdit,
  today,
  onClose,
  onEdit,
  onDeleted,
}: {
  id: number;
  mayEdit: boolean;
  today: string;
  onClose: () => void;
  onEdit: (t: ApiTask) => void;
  onDeleted: (t: ApiTask, ids: number[]) => void;
}) {
  const q = useTask(id);
  const w = useTaskWrites();
  const t = q.data;
  const [note, setNote] = useState('');
  const [follow, setFollow] = useState(false);
  const [proof, setProof] = useState('');
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<'none' | 'complete' | 'exception' | 'delete'>('none');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [ask, setAsk] = useState<null | 'close' | 'complete'>(null);
  const [scope, setScope] = useState<'one' | 'future'>('one');
  const askRef = useRef<HTMLDivElement>(null);
  const noteDirty = note.trim().length > 0;

  const fail = (e: unknown, field = 'form') => {
    if (e instanceof ApiError && (e.code === 'task_changed' || e.code === 'task_already_done')) {
      setErrors({ form: T('Oppgaven er endret av noen andre. Den viste versjonen er nå oppdatert.') });
      void q.refetch();
      return;
    }
    const f = e instanceof ApiError ? ((e.body as { field?: string })?.field ?? field) : field;
    setErrors({
      [f === 'measurement' || f === 'comment' ? 'proof' : f]:
        e instanceof Error ? e.message : T('Kunne ikke lagre.'),
    });
    requestAnimationFrame(() =>
      document.getElementById(`opd-${f === 'measurement' || f === 'comment' ? 'proof' : f}`)?.focus(),
    );
  };

  const addNote = async () => {
    if (!t) return false;
    if (!note.trim()) {
      setErrors({ note: T('Skriv et notat først.') });
      requestAnimationFrame(() => document.getElementById('opd-note')?.focus());
      return false;
    }
    try {
      await w.comment.mutateAsync({ id: t.id, text: note.trim(), follow_up: follow });
      setNote('');
      setFollow(false);
      setErrors({});
      return true;
    } catch (e) {
      fail(e, 'note');
      return false;
    }
  };

  const complete = async (exception: boolean) => {
    if (!t) return;
    if (noteDirty && ask !== 'complete') {
      setAsk('complete');
      requestAnimationFrame(() => askRef.current?.focus());
      return;
    }
    setAsk(null);
    if (exception && reason.trim().length < 5) {
      setErrors({ reason: T('Skriv en begrunnelse på minst fem tegn.') });
      requestAnimationFrame(() => document.getElementById('opd-reason')?.focus());
      return;
    }
    const body: {
      expected_revision: number;
      comment?: string;
      measurement?: number;
      exception_reason?: string;
    } = {
      expected_revision: t.revision,
    };
    if (exception) body.exception_reason = reason.trim();
    else if (t.requirement === 'measurement') {
      const n = Number(proof.trim().replace(',', '.'));
      if (!proof.trim() || !Number.isFinite(n)) {
        setErrors({ proof: T('Skriv målingen som et tall. 0 er en gyldig måling.') });
        requestAnimationFrame(() => document.getElementById('opd-proof')?.focus());
        return;
      }
      body.measurement = n;
    } else if (t.requirement === 'comment') {
      if (!proof.trim()) {
        setErrors({ proof: T('Oppgaven krever en kommentar før den kan fullføres.') });
        requestAnimationFrame(() => document.getElementById('opd-proof')?.focus());
        return;
      }
      body.comment = proof.trim();
    } else if (proof.trim()) body.comment = proof.trim();
    try {
      await w.complete.mutateAsync({ id: t.id, body });
      setMode('none');
      setProof('');
      setReason('');
      setErrors({});
      toast(exception ? T('Fullført med lederunntak') : T('Oppgaven er utført'));
    } catch (e) {
      fail(e, exception ? 'reason' : 'proof');
    }
  };

  const remove = async () => {
    if (!t) return;
    try {
      const r = await w.remove.mutateAsync({
        id: t.id,
        revision: t.revision,
        ...(scope === 'future' ? { scope } : {}),
      });
      onDeleted(t, r.ids);
    } catch (e) {
      fail(e);
    }
  };

  const blockClose = () => {
    if (!noteDirty || ask === 'close') return false;
    setAsk('close');
    requestAnimationFrame(() => askRef.current?.focus());
    return true;
  };

  const shown = t ? shownOf(t, today) : 'open';
  const err = (k: string) =>
    errors[k] ? (
      <span className="o-err" id={`opd-${k}-err`} role="alert">
        {errors[k]}
      </span>
    ) : null;

  return (
    <Drawer
      open
      onClose={onClose}
      blockClose={blockClose}
      className="opgd"
      title={t?.title ?? T('Oppgave')}
      sub={mayEdit ? undefined : T('Visning. Du kan se oppgaven, men ikke endre den.')}
    >
      {!t ? (
        q.isError ? (
          <p className="o-err" role="alert">
            {T('Fant ikke oppgaven. Den kan være slettet.')}
          </p>
        ) : (
          <Skeleton rows={4} />
        )
      ) : (
        <>
          {ask && (
            <div
              className="o-decide"
              role="alertdialog"
              tabIndex={-1}
              ref={askRef}
              aria-label={T('Ulagret notat')}
            >
              <b>{T('Du har skrevet et notat som ikke er lagt til.')}</b>
              <div className="o-row" style={{ marginTop: 8 }}>
                <Button
                  small
                  tone="pri"
                  onClick={async () => {
                    if (!(await addNote())) return;
                    if (ask === 'complete') {
                      setAsk('complete');
                      await complete(mode === 'exception');
                    } else {
                      setAsk(null);
                      onClose();
                    }
                  }}
                >
                  {ask === 'complete' ? T('Legg til notatet og fortsett') : T('Legg til notatet og lukk')}
                </Button>
                <Button
                  small
                  onClick={() => {
                    setNote('');
                    if (ask === 'close') onClose();
                    setAsk(null);
                  }}
                >
                  {T('Forkast notatet')}
                </Button>
                <Button small ghost onClick={() => setAsk(null)}>
                  {T('Avbryt')}
                </Button>
              </div>
            </div>
          )}
          {errors.form && (
            <p className="o-err" role="alert" style={{ marginBottom: 12 }}>
              {errors.form}
            </p>
          )}
          <dl className="o-facts">
            <dt>{T('Status')}</dt>
            <dd data-testid="opd-status">
              {T(SHOWN_LABEL[shown])}
              {t.status === 'exception' && t.exception_reason ? `: ${t.exception_reason}` : ''}
            </dd>
            <dt>{T('Ansvarlig')}</dt>
            <dd>{t.assignee_name ?? T('Uten ansvarlig')}</dd>
            {t.done_by_name && (
              <>
                <dt>{T('Utført av')}</dt>
                <dd>
                  {t.done_by_name}
                  {t.done_at ? `, ${when(t.done_at)}` : ''}
                </dd>
              </>
            )}
            <dt>{t.due_date === t.start_date ? T('Dato') : T('Periode')}</dt>
            <dd>
              {t.due_date === t.start_date
                ? dayMonth(t.start_date)
                : T('{from} til frist {to}', { from: dayMonth(t.start_date), to: dayMonth(t.due_date) })}
            </dd>
            <dt>{T('Klokkeslett')}</dt>
            <dd>{t.start_time ?? T('Ikke satt, i løpet av dagen')}</dd>
            {t.estimate_minutes != null && (
              <>
                <dt>{T('Estimat')}</dt>
                <dd>{`${estimateOf(t.estimate_minutes)} · ${T('planlegging, ikke registrert tid')}`}</dd>
              </>
            )}
            <dt>{T('Dokumentasjon')}</dt>
            <dd>
              {t.requirement === 'comment'
                ? T('Kommentar')
                : t.requirement === 'measurement'
                  ? T('Måling (tall)')
                  : T('Ingen krav')}
            </dd>
            <dt>{T('Vakt')}</dt>
            <dd data-testid="opd-shift">
              {t.shift
                ? `${t.shift.start}–${t.shift.end}${t.shift.type_name ? ` · ${t.shift.type_name}` : ''}`
                : T('Uten vakt. Ingen vakt eller arbeidstid er opprettet.')}
            </dd>
            {t.type && (
              <>
                <dt>{T('Type')}</dt>
                <dd>{t.type.name}</dd>
              </>
            )}
            {t.series_id != null && (
              <>
                <dt>{T('Kilde')}</dt>
                <dd>{T('Gjentakende oppgave')}</dd>
              </>
            )}
          </dl>

          {mayEdit && (
            <div className="o-row" style={{ marginBottom: 14 }}>
              {t.status === 'open' ? (
                <>
                  <Button
                    tone="pri"
                    small
                    onClick={() => setMode(mode === 'complete' ? 'none' : 'complete')}
                    aria-expanded={mode === 'complete'}
                  >
                    {T('Fullfør')}
                  </Button>
                  <Button
                    small
                    onClick={() => setMode(mode === 'exception' ? 'none' : 'exception')}
                    aria-expanded={mode === 'exception'}
                  >
                    {T('Lederunntak')}
                  </Button>
                </>
              ) : (
                <Button
                  small
                  onClick={async () => {
                    try {
                      await w.reopen.mutateAsync({ id: t.id, revision: t.revision });
                      toast(T('Oppgaven er åpnet igjen. Tidligere dokumentasjon er beholdt.'));
                    } catch (e) {
                      fail(e);
                    }
                  }}
                >
                  {T('Gjenåpne')}
                </Button>
              )}
              <Button small onClick={() => onEdit(t)}>
                {T('Endre')}
              </Button>
              <Button
                small
                tone="dgr"
                ghost
                onClick={() => setMode(mode === 'delete' ? 'none' : 'delete')}
                aria-expanded={mode === 'delete'}
              >
                {T('Slett')}
              </Button>
            </div>
          )}

          {mode === 'complete' && t.status === 'open' && (
            <div className="o-box">
              <label className="o-fld" style={{ marginBottom: 8 }}>
                <span>
                  {t.requirement === 'measurement'
                    ? T('Måling (påkrevd)')
                    : t.requirement === 'comment'
                      ? T('Kommentar (påkrevd)')
                      : T('Kommentar (valgfritt)')}
                </span>
                <input
                  id="opd-proof"
                  inputMode={t.requirement === 'measurement' ? 'decimal' : undefined}
                  value={proof}
                  aria-invalid={errors.proof ? true : undefined}
                  aria-describedby={errors.proof ? 'opd-proof-err' : undefined}
                  onChange={(e) => {
                    setProof(e.target.value);
                    setErrors({});
                  }}
                />
                {err('proof')}
              </label>
              <Button tone="pri" small onClick={() => complete(false)} disabled={w.complete.isPending}>
                {T('Marker som utført')}
              </Button>
            </div>
          )}
          {mode === 'exception' && t.status === 'open' && (
            <div className="o-box">
              <label className="o-fld" style={{ marginBottom: 8 }}>
                <span>{T('Begrunnelse for lederunntak')}</span>
                <textarea
                  id="opd-reason"
                  rows={2}
                  value={reason}
                  aria-invalid={errors.reason ? true : undefined}
                  aria-describedby={errors.reason ? 'opd-reason-err' : 'opd-reason-hint'}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setErrors({});
                  }}
                />
                {err('reason') ?? (
                  <span className="o-hint" id="opd-reason-hint">
                    {T('Minst fem tegn. Lagres med ditt navn og tidspunktet.')}
                  </span>
                )}
              </label>
              <Button small onClick={() => complete(true)} disabled={w.complete.isPending}>
                {T('Fullfør med unntak')}
              </Button>
            </div>
          )}
          {mode === 'delete' && (
            <div className="o-decide">
              <b>{T('Slette «{title}»?', { title: t.title })}</b>
              {t.series_id != null && (
                <div style={{ margin: '8px 0' }}>
                  <label className="o-check" style={{ marginBottom: 4 }}>
                    <input
                      type="radio"
                      name="opd-del"
                      checked={scope === 'one'}
                      onChange={() => setScope('one')}
                    />
                    {T('Bare denne')}
                  </label>
                  <label className="o-check" style={{ marginBottom: 4 }}>
                    <input
                      type="radio"
                      name="opd-del"
                      checked={scope === 'future'}
                      onChange={() => setScope('future')}
                    />
                    {T('Denne og senere åpne i serien')}
                  </label>
                </div>
              )}
              <p className="o-hint">{T('Notater og historikk følger med. Du kan angre i ti minutter.')}</p>
              <div className="o-row" style={{ marginTop: 8 }}>
                <Button small tone="dgr" onClick={remove}>
                  {T('Slett')}
                </Button>
                <Button small onClick={() => setMode('none')}>
                  {T('Avbryt')}
                </Button>
              </div>
            </div>
          )}

          <h4>{T('Historikk')}</h4>
          <ul className="o-hist" data-testid="opd-history">
            {(q.data?.events ?? []).map((e) => (
              <li key={e.id}>
                <b>{T(KIND[e.kind])}</b>
                {e.reason ? `: ${e.reason}` : ''}
                {e.comment ? `: ${e.comment}` : ''}
                {e.measurement != null ? `: ${String(e.measurement).replace('.', ',')}` : ''}
                <small>{[e.actor_name, when(e.at)].filter(Boolean).join(', ')}</small>
              </li>
            ))}
          </ul>

          {mayEdit && (
            <>
              <label className="o-fld">
                <span>{T('Notat')}</span>
                <textarea
                  id="opd-note"
                  rows={2}
                  value={note}
                  aria-invalid={errors.note ? true : undefined}
                  aria-describedby={errors.note ? 'opd-note-err' : undefined}
                  onChange={(e) => {
                    setNote(e.target.value);
                    setErrors({});
                  }}
                />
                {err('note')}
              </label>
              <label className="o-check">
                <input type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)} />
                {T('Merk som oppfølging. Ingen melding sendes; notatet vises som ikke sendt.')}
              </label>
              <Button small onClick={addNote} disabled={w.comment.isPending}>
                {T('Legg til notat')}
              </Button>
            </>
          )}
        </>
      )}
    </Drawer>
  );
}
